import { FormattedMessage, useIntl } from "react-intl";
import {
  useEventStore,
  useGuestStore,
  useTimeToReleaseUploadStore,
} from "store";
import { User } from "assets/svg";
import { availableLanguages, languageLabels } from "i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { clarity } from "react-microsoft-clarity";

import bingoBlackIcon from "../../assets/images/bingo-black-icon.png";
import bingoRedIcon from "../../assets/images/bingo-red-icon.png";
import feedBlackIcon from "../../assets/images/feed-black-icon.png";
import feedRedIcon from "../../assets/images/feed-red-icon.png";
import foodMenuBlackIcon from "../../assets/images/food-menu-black-icon.png";
import foodMenuRedIcon from "../../assets/images/food-menu-red-icon.png";
import galleryBlackIcon from "../../assets/images/gallery-black-icon.png";
import galleryRedIcon from "../../assets/images/gallery-red-icon.png";
import photoChallengeBlackIcon from "../../assets/images/photo-challenge-black-icon.png";
import photoChallengeRedIcon from "../../assets/images/photo-challenge-red-icon.png";
import quizBlackIcon from "../../assets/images/quiz-black-icon.png";
import quizRedIcon from "../../assets/images/quiz-red-icon.png";
import recorderBlackIcon from "../../assets/images/recorder-black-icon.png";
import recorderRedIcon from "../../assets/images/recorder-red-icon.png";
import scheduleBlackIcon from "../../assets/images/schedule-black-icon.png";
import scheduleRedIcon from "../../assets/images/schedule-red-icon.png";
import teamMatchBlackIcon from "../../assets/images/team-match-black-icon.png";
import teamMatchRedIcon from "../../assets/images/team-match-red-icon.png";
import uploaderBlackIcon from "../../assets/images/uploader-black-icon.png";
import uploaderRedIcon from "../../assets/images/uploader-red-icon.png";
import newPostRedIcon from "../../assets/images/new-post-red-icon.png";
import newPostBlackIcon from "../../assets/images/new-post-black-icon.png";
import logoRedIcon from "../../assets/images/logo-rose-icon.png";
import logoBlackIcon from "../../assets/images/logo-black-icon.png";
import shareRedIcon from "../../assets/images/share-red-icon.png";
import shareBlackIcon from "../../assets/images/share-black-icon.png";
import { FeedModal } from "./FeedModal";
import TopBar from "./TopBar";
import { shareLink } from "utils/utils";
import { useLogEvent } from "hooks/useLogEvent";
import TimeToShareModal from "../../components/TimeToShareModal";
import GuestFormModal from "components/GuestFormModal";

const BLACK_ICONS = {
  newPost: newPostBlackIcon,
  bingo: bingoBlackIcon,
  feed: feedBlackIcon,
  food_menu: foodMenuBlackIcon,
  gallery: galleryBlackIcon,
  photo_challenge: photoChallengeBlackIcon,
  quiz: quizBlackIcon,
  recorder: recorderBlackIcon,
  schedule: scheduleBlackIcon,
  team_match: teamMatchBlackIcon,
  uploader: uploaderBlackIcon,
  logo: logoBlackIcon,
  share: shareBlackIcon,
};

const RED_ICONS = {
  newPost: newPostRedIcon,
  bingo: bingoRedIcon,
  feed: feedRedIcon,
  food_menu: foodMenuRedIcon,
  gallery: galleryRedIcon,
  photo_challenge: photoChallengeRedIcon,
  quiz: quizRedIcon,
  recorder: recorderRedIcon,
  schedule: scheduleRedIcon,
  team_match: teamMatchRedIcon,
  uploader: uploaderRedIcon,
  logo: logoRedIcon,
  share: shareRedIcon,
};

interface OptionCardMenuProps {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}
export const OptionCardMenu = ({
  icon,
  title,
  description,
  onClick,
}: OptionCardMenuProps) => {
  const { theme } = useEventStore();

  return (
    <button
      type="button"
      className={`w-full flex items-center px-5 py-2 gap-2 card ${
        theme.card
      } transition-transform duration-200 shadow-md hover:shadow-lg ${
        theme.name === "white" && `border-[1px] border-gray-100`
      } `}
      onClick={onClick}
    >
      <img
        src={icon}
        alt={title}
        className="w-14 h-14 transition-transform max-h-16 object-contain"
      />
      <div className="flex flex-col items-center grow">
        <h3 className="text-base font-semibold text-theme-black text-center -mb-0.5">
          {title}
        </h3>
        <p className="text-theme-black text-xs w-48">{description}</p>
      </div>
    </button>
  );
};

export default function FeedMenu() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { event, theme, language, setLanguage, publicUrl } = useEventStore();
  const { guest } = useGuestStore();
  const [guestPhotoUrl, setGuestPhotoUrl] = useState<string | null>(null);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(
    language
  );
  const [chooseTimeToShareModalOpen, setChooseTimeToShareModalOpen] =
    useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const { setTimeToReleaseChoice } = useTimeToReleaseUploadStore();
  const { logShareButton } = useLogEvent();

  useEffect(() => {
    const fetchGuestPhoto = async () => {
      if (guest?.photoStoragePath) {
        try {
          const url = await SpacesService.getFileUrl(
            guest.photoStoragePath as string
          );
          setGuestPhotoUrl(url);
        } catch (error) {
          console.error("Failed to load guest photo:", error);
          setGuestPhotoUrl(null);
        }
      } else {
        setGuestPhotoUrl(null);
      }
    };

    fetchGuestPhoto();
  }, [guest, guest?.photoStoragePath]);

  const menuIcons = useMemo(
    () => (theme.name === "rose" ? RED_ICONS : BLACK_ICONS),
    [theme]
  );

  const bingoGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("bingo"),
    [event]
  );

  const photoChallengeGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("photo_challenge"),
    [event]
  );

  const quizGameEnabled = useMemo(
    () => event?.betaFeatures?.includes("quiz"),
    [event]
  );

  const teamMatchEnabled = useMemo(
    () => event?.betaFeatures?.includes("team_match"),
    [event]
  );

  const hasAnyGameEnabled = useMemo(
    () =>
      bingoGameEnabled ||
      photoChallengeGameEnabled ||
      quizGameEnabled ||
      teamMatchEnabled,
    [
      bingoGameEnabled,
      photoChallengeGameEnabled,
      quizGameEnabled,
      teamMatchEnabled,
    ]
  );

  const foodMenuEnabled = useMemo(
    () =>
      event?.betaFeatures?.includes("uploads_in_menu") &&
      event?.menuUploads?.find((menu) => menu.id === "food_menu"),
    [event]
  );

  const scheduleEnabled = useMemo(
    () =>
      event?.betaFeatures?.includes("uploads_in_menu") &&
      event?.menuUploads?.find((menu) => menu.id === "schedule"),
    [event]
  );

  const hasEventInfoEnabled = useMemo(
    () => foodMenuEnabled || scheduleEnabled,
    [foodMenuEnabled, scheduleEnabled]
  );

  const openUrlInNewTab = (url: string) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
      }, 100);
    } else {
      window.open(url, "_blank");
    }
  };

  const handleCreateNewPostClick = () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_new_post", "clicked");
    }
    navigate("/feed/v2", { state: { openCreatePost: true } });
  };

  const handleRecordClick = useCallback(() => {
    if (event?.uploadsReleaseMode === "mixedRelease") {
      setChooseTimeToShareModalOpen(true);
      console.log(chooseTimeToShareModalOpen, setChooseTimeToShareModalOpen);
    } else {
      if (event?.uploadsReleaseMode === "realtimeRelease") {
        setTimeToReleaseChoice("release-now");
      }
      if (event?.uploadsReleaseMode === "oneYearCapsuleRelease") {
        setTimeToReleaseChoice("release-later");
      }
      navigate("/record/form");
    }
  }, [
    event,
    navigate,
    setChooseTimeToShareModalOpen,
    setTimeToReleaseChoice,
    chooseTimeToShareModalOpen,
  ]);

  const handleUploadClick = () => {
    navigate("../../upload");
  };

  const handleGalleryClick = useCallback(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_gallery", "clicked");
    }
    const eventGalleryLink = `https://app.capsuladotempo.com/public/${event?.code}/gallery`;
    openUrlInNewTab(eventGalleryLink);
  }, [event]);

  const handleBingoClick = () => {
    navigate("../../games/bingo");
  };

  const handlePhotoChallengeClick = () => {
    navigate("../../games/photo-challenge");
  };

  const handleQuizClick = () => {
    navigate("../../games/quiz");
  };

  const handleTeamMatchClick = () => {
    navigate("../../games/team-match");
  };

  const handleFoodMenuClick = useCallback(async () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_food_menu", "clicked");
    }

    const uploadItem = event?.menuUploads?.find(
      (menu) => menu.id === "food_menu"
    );

    if (!uploadItem) return;

    const url = await SpacesService.getFileUrl(
      uploadItem.storagePath as string
    );

    openUrlInNewTab(url);
  }, [event]);

  const handleScheduleClick = useCallback(async () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_schedule", "clicked");
    }

    const uploadItem = event?.menuUploads?.find(
      (menu) => menu.id === "schedule"
    );

    if (!uploadItem) return;

    const url = await SpacesService.getFileUrl(
      uploadItem.storagePath as string
    );

    openUrlInNewTab(url);
  }, [event]);

  const handleGuestInfoClick = () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_guest_info", "clicked");
    }
    setGuestModalOpen(true);
  };

  const handleGuestModalClose = () => {
    setGuestModalOpen(false);
  }

  const handleLanguageClick = () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_language", "clicked");
    }
    setLanguageModalOpen(true);
  };

  const handleShareEventLink = useCallback(async () => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_share", "clicked");
    }

    try {
      const text = intl.formatMessage({
        id: "message.shareLink",
        defaultMessage: "Compartilhe seus momentos favoritos da festa!",
      });
      await shareLink(publicUrl, text, logShareButton);
    } catch (error) {
      if (error instanceof Error && error.message === "Share canceled") {
        console.log("Compartilhamento cancelado pelo usuário");
        return;
      }
      console.error("Erro ao compartilhar:", error);
    }
  }, [intl, publicUrl, logShareButton]);

  const handleLandingPageClick = useCallback(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed_menu_about", "clicked");
    }
    const capsulaLadingPage = "https://videorecados.capsuladotempo.com";
    openUrlInNewTab(capsulaLadingPage);
  }, []);

  return (
    <>
      <TopBar />
      <div
        className={`${theme.background} theme-layout px-2 pt-14 pb-16 mx-0 shadow-xl flex flex-col items-stretch w-full h-full`}
      >
        <div className="flex flex-col gap-10">
          <section>
            <p className="text-start text-sm font-century-gothic-bold pb-2">
              <FormattedMessage
                id="feed.menu.title.interaction"
                defaultMessage="Enviar arquivos"
              />
            </p>
            <div className="flex flex-col items-center gap-3 grow flex-1 w-full">
              <OptionCardMenu
                icon={menuIcons.newPost}
                title={intl.formatMessage({
                  id: "feed.menu.button.options.newPost",
                  defaultMessage: "Criar post",
                })}
                description={intl.formatMessage({
                  id: "feed.menu.message.options.newPost",
                  defaultMessage: "Crie um post agora mesmo!",
                })}
                onClick={handleCreateNewPostClick}
              />

              <OptionCardMenu
                icon={menuIcons.recorder}
                title={intl.formatMessage({
                  id: "feed.menu.button.options.record",
                  defaultMessage: "Gravar agora",
                })}
                description={intl.formatMessage({
                  id: "feed.menu.message.options.record",
                  defaultMessage: "Grave agora sua mensagem especial!",
                })}
                onClick={handleRecordClick}
              />

              <OptionCardMenu
                icon={menuIcons.uploader}
                title={intl.formatMessage({
                  id: "feed.menu.button.options.upload",
                  defaultMessage: "Escolher arquivos",
                })}
                description={intl.formatMessage({
                  id: "feed.menu.message.options.upload",
                  defaultMessage:
                    "Compartilhe as melhores fotos e vídeos da festa!",
                })}
                onClick={handleUploadClick}
              />
            </div>
          </section>
          {event?.publicGallery && (
            <section>
              <p className="text-start text-sm font-century-gothic-bold pb-2">
                <FormattedMessage
                  id="feed.menu.title.gallery"
                  defaultMessage="Galeria da festa"
                />
              </p>

              <div className="flex flex-col items-center gap-3 grow flex-1 w-full">
                <OptionCardMenu
                  icon={menuIcons.gallery}
                  title={intl.formatMessage({
                    id: "feed.menu.button.options.gallery",
                    defaultMessage: "Ver álbum",
                  })}
                  description={intl.formatMessage({
                    id: "feed.menu.message.options.gallery",
                    defaultMessage: "Veja as fotos do evento!",
                  })}
                  onClick={handleGalleryClick}
                />
              </div>
            </section>
          )}
          {hasAnyGameEnabled && (
            <section>
              <p className="text-start text-sm font-century-gothic-bold pb-2">
                <FormattedMessage
                  id="feed.menu.title.games"
                  defaultMessage="Jogos"
                />
              </p>
              <div className="flex flex-col items-center gap-3 grow flex-1 w-full">
                {bingoGameEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.bingo}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.bingoGame",
                      defaultMessage: "Bingo",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.bingoGame",
                      defaultMessage: "Jogue o bingo da festa!",
                    })}
                    onClick={handleBingoClick}
                  />
                )}
                {photoChallengeGameEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.photo_challenge}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.photoChallengeGame",
                      defaultMessage: "Desafio de fotos",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.photoChallengeGame",
                      defaultMessage: "Participe do desafio de fotos!",
                    })}
                    onClick={handlePhotoChallengeClick}
                  />
                )}
                {quizGameEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.quiz}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.quizGame",
                      defaultMessage: "Quiz",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.quizGame",
                      defaultMessage: "Teste seus conhecimentos!",
                    })}
                    onClick={handleQuizClick}
                  />
                )}
                {teamMatchEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.team_match}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.teamMatchGame",
                      defaultMessage: "Batalha dos Noivos",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.teamMatchGame",
                      defaultMessage: "De qual time você faz parte?",
                    })}
                    onClick={handleTeamMatchClick}
                  />
                )}
              </div>
            </section>
          )}
          {hasEventInfoEnabled && (
            <section>
              <p className="text-start text-sm font-century-gothic-bold pb-2">
                <FormattedMessage
                  id="feed.menu.title.info"
                  defaultMessage="Sobre o evento"
                />
              </p>
              <div className="flex flex-col items-center gap-3 grow flex-1 w-full">
                {foodMenuEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.food_menu}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.foodMenu",
                      defaultMessage: "Cardápio",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.foodMenu",
                      defaultMessage: "Veja o cardápio da festa!",
                    })}
                    onClick={handleFoodMenuClick}
                  />
                )}
                {scheduleEnabled && (
                  <OptionCardMenu
                    icon={menuIcons.schedule}
                    title={intl.formatMessage({
                      id: "feed.menu.button.options.schedule",
                      defaultMessage: "Programação",
                    })}
                    description={intl.formatMessage({
                      id: "feed.menu.message.options.schedule",
                      defaultMessage: "Confira a programação da festa!",
                    })}
                    onClick={handleScheduleClick}
                  />
                )}
              </div>
            </section>
          )}
          <section>
            <p className="text-start text-sm font-century-gothic-bold pb-2">
              <FormattedMessage
                id="feed.menu.title.guestInfo"
                defaultMessage="Suas informações"
              />
            </p>
            <div className="flex flex-col items-center gap-3 grow flex-1 w-full">
              <button
                type="button"
                className={`w-full flex items-center px-5 py-2 gap-2 card ${
                  theme.card
                } transition-transform duration-200 shadow-md hover:shadow-lg ${
                  theme.name === "white" && `border-[1px] border-gray-100`
                }`}
                onClick={handleGuestInfoClick}
              >
                <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                  {guestPhotoUrl ? (
                    <img
                      src={guestPhotoUrl}
                      alt={guest?.name || "Convidado anônimo"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 flex items-center justify-center">
                      <User
                        width={24}
                        height={24}
                        color="none"
                        outline="#414141"
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-center grow">
                  <h3 className="text-base font-semibold text-theme-black text-center -mb-0.5">
                    {guest?.name ||
                      intl.formatMessage({
                        id: "feed.menu.options.anonymousGuest",
                        defaultMessage: "Convidado anônimo",
                      })}
                  </h3>
                  <p className="text-theme-black text-xs w-48">
                    {intl.formatMessage({
                      id: "feed.menu.options.guestEditInfo",
                      defaultMessage: "Clique para editar suas informações!",
                    })}
                  </p>
                </div>
              </button>
            </div>
          </section>

          <section>
            <p className="text-start text-sm font-century-gothic-bold pb-2">
              <FormattedMessage
                id="feed.menu.title.other"
                defaultMessage="Outros"
              />
            </p>

            <div className="flex flex-col items-center gap-3 grow flex-1  w-full">
              <>
                <button
                  type="button"
                  className={`w-full flex items-center px-5 py-2 gap-2 card ${
                    theme.card
                  } transition-transform duration-200 shadow-md hover:shadow-lg ${
                    theme.name === "white" && `border-[1px] border-gray-100`
                  }`}
                  onClick={handleLanguageClick}
                >
                  <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                    <span className="text-theme-black font-semibold">
                      {language.split("-")[0].toUpperCase()}
                    </span>
                  </div>

                  <div className="flex flex-col items-center gap-1 grow">
                    <h3 className="text-base font-semibold text-theme-black text-center -mb-0.5">
                      <FormattedMessage
                        id="feed.menu.button.options.language"
                        defaultMessage="Idioma"
                      />
                    </h3>
                    <p className="text-theme-black text-xs w-48">
                      {intl.formatMessage({
                        id: "feed.menu.message.options.languageInfo",
                        defaultMessage: "Clique para alterar o idioma!",
                      })}
                    </p>
                  </div>
                </button>
                <FeedModal
                  isOpen={languageModalOpen}
                  onClose={() => setLanguageModalOpen(false)}
                >
                  <div className="flex flex-col gap-4 w-full h-[70vh] p-2">
                    <div className="overflow-y-auto flex flex-col gap-2 p-2">
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang}
                          className={`w-full flex items-center px-5 py-2 gap-2 card ${
                            theme.card
                          } transition-all duration-200 shadow-md hover:shadow-lg ${
                            pendingLanguage === lang
                              ? theme.name === "rose"
                                ? "ring-2 ring-primary-rose"
                                : "ring-2 ring-gray-600"
                              : ""
                          }`}
                          onClick={() => {
                            setPendingLanguage(lang);
                          }}
                        >
                          <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                            {(() => {
                              const idx = lang.indexOf("-");
                              return idx !== -1 ? lang.slice(idx + 1) : "";
                            })()}
                          </div>

                          <div className="flex flex-col items-center gap-1 grow">
                            <h3 className="text-base font-semibold text-theme-black text-center">
                              {languageLabels[lang] || lang}
                            </h3>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-4 my-4">
                      <button
                        className={`${theme.button} `}
                        onClick={() => {
                          setLanguageModalOpen(false);
                          setPendingLanguage(language);
                        }}
                      >
                        <FormattedMessage
                          id="feed.button.options.cancel"
                          defaultMessage="Cancelar"
                        />
                      </button>
                      <button
                        className={`${theme.button}`}
                        onClick={() => {
                          if (pendingLanguage) {
                            setLanguage(pendingLanguage);
                            setLanguageModalOpen(false);
                          }
                        }}
                        disabled={!pendingLanguage}
                      >
                        <FormattedMessage
                          id="feed.button.options.confirm"
                          defaultMessage="Confirmar"
                        />
                      </button>
                    </div>
                  </div>
                </FeedModal>
              </>

              <OptionCardMenu
                icon={menuIcons.share}
                title={intl.formatMessage({
                  id: "feed.menu.button.options.share",
                  defaultMessage: "Compartilhar",
                })}
                description={intl.formatMessage({
                  id: "feed.menu.message.options.shareInfo",
                  defaultMessage: "Compartilhe o link do evento!",
                })}
                onClick={handleShareEventLink}
              />

              <OptionCardMenu
                icon={menuIcons.logo}
                title={intl.formatMessage({
                  id: "feed.menu.button.options.about",
                  defaultMessage: "Sobre a Cápsula do Tempo",
                })}
                description={intl.formatMessage({
                  id: "feed.menu.message.options.about",
                  defaultMessage: "Conheça a Cápsula do Tempo!",
                })}
                onClick={handleLandingPageClick}
              />
            </div>
          </section>
        </div>
        <TimeToShareModal
          setChooseTimeToShareModalOpen={setChooseTimeToShareModalOpen}
          chooseTimeToShareModalOpen={chooseTimeToShareModalOpen}
        />
        <GuestFormModal
          isOpen={guestModalOpen}
          onClose={handleGuestModalClose}
        />
      </div>
    </>
  );
}
